/**
 * "The Fog Lifts" — replay controller and UI state. After a war the host sends its ReplayLog
 * (`{ t: 'replay' }`, see net/router.ts); `openReplay()` starts a replay worker, parks the live
 * game store (the after-action report comes back untouched on close), and streams frames from
 * the worker into `gameStore` so the ordinary renderer, minimap, teletype and unit card draw them.
 *
 * Playback is pull-based: one `show` request in flight at a time; the next is scheduled so that
 * frames arrive every `BASE_TICK_MS / speed` ms, and the renderer interpolates between them over
 * the same interval. A seek or a change of perspective is a "jump" (no interpolation, the
 * teletype is refilled with that perspective's recent dispatches).
 *
 * A film from the archive (a saved `.friction-replay`, `openArchiveReplay`) plays the same way;
 * closing it puts back whatever log the last war left.
 */
import {
  type FactionId,
  type GameMap,
  mapForConfig,
  type ReplayLog,
  SPECTATOR,
  type Tick,
} from '@friction/engine';
import { createStore, useStore } from 'zustand';
import { type GameStoreState, gameStore } from '../game/store';
import type { ReplayReply, ReplayRequest } from './protocol';
import { forTheRecord, type ReplayTimeline } from './timeline';

export const REPLAY_SPEEDS = [1, 2, 4, 8, 16] as const;
export type ReplaySpeed = (typeof REPLAY_SPEEDS)[number];
/** Real milliseconds per in-game hour at 1× (the live game's speed II). */
export const BASE_TICK_MS = 1000;

export interface ReplayState {
  /** Replay log of the match that just ended (null before the war is over). */
  log: ReplayLog | null;
  /** The replay screen is open. */
  active: boolean;
  /** Where the film came from: the war just fought, or a record from the film archive. */
  source: 'live' | 'archive';
  status: 'idle' | 'loading' | 'ready' | 'error';
  /** Loading progress 0..1 (the worker's first pass through the war). */
  progress: number;
  error: string | null;
  timeline: ReplayTimeline | null;
  /** The re-simulation reproduced the recorded end state (null until known). */
  verified: boolean | null;
  /** Tick of the frame on screen. */
  tick: Tick;
  playing: boolean;
  speed: ReplaySpeed;
  /** SPECTATOR (omniscient) or the faction whose knowledge is shown. */
  perspective: FactionId;
  /** In a faction's perspective: also pencil in where its enemies really stood. */
  truth: boolean;
}

const initial = {
  active: false,
  source: 'live' as 'live' | 'archive',
  status: 'idle' as const,
  progress: 0,
  error: null,
  timeline: null,
  verified: null,
  tick: 0,
  playing: false,
  perspective: SPECTATOR,
};

export const replayStore = createStore<ReplayState>()(() => ({
  log: null,
  ...initial,
  speed: 8 as ReplaySpeed,
  truth: true,
}));

export function useReplay<T>(selector: (s: ReplayState) => T): T {
  return useStore(replayStore, selector);
}

// ---- worker plumbing ----------------------------------------------------------------------------

let worker: Worker | null = null;
/** The live game store as it was when the replay opened (restored on close). */
let parked: GameStoreState | null = null;
/** The last war's log, put back when an archive film closes. */
let liveLog: ReplayLog | null = null;
let map: GameMap | null = null;
let seq = 0;
let inFlight: { seq: number; jump: boolean } | null = null;
let pending: { tick: Tick; jump: boolean } | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let requestedAt = 0;
let unwatch: (() => void) | null = null;

const interval = () => BASE_TICK_MS / replayStore.getState().speed;

function spawnWorker(): Worker {
  // Literal `new Worker(new URL(...), { type: 'module' })` so Vite bundles the worker.
  return new Worker(new URL('./replay.worker.ts', import.meta.url), {
    type: 'module',
    name: 'friction-replay',
  });
}

function post(msg: ReplayRequest): void {
  worker?.postMessage(msg);
}

function cancelTimer(): void {
  if (timer !== null) clearTimeout(timer);
  timer = null;
}

/** Ask the worker for `tick`; coalesces while a request is in flight (the latest wins). */
function request(tick: Tick, jump: boolean): void {
  const s = replayStore.getState();
  if (!worker || s.status !== 'ready' || !s.timeline) return;
  const t = Math.max(0, Math.min(s.timeline.endTick, Math.round(tick)));
  if (inFlight) {
    pending = { tick: t, jump: jump || (pending?.jump ?? false) };
    return;
  }
  cancelTimer();
  inFlight = { seq: ++seq, jump };
  requestedAt = performance.now();
  post({
    t: 'show',
    seq,
    tick: t,
    perspective: s.perspective,
    jump,
    truth: s.truth && s.perspective !== SPECTATOR,
  });
}

function onFrame(msg: Extract<ReplayReply, { t: 'frame' }>): void {
  if (!inFlight || msg.seq !== inFlight.seq || !map) return;
  const { jump } = inFlight;
  inFlight = null;
  const s = replayStore.getState();
  const last = s.timeline?.endTick ?? msg.view.tick;
  const playing = s.playing && msg.view.tick < last;
  gameStore.getState().receiveReplayFrame({
    map,
    config: s.log!.config,
    view: msg.view,
    // The last frame still glides into place; a pending jump will replace it at once.
    tickMs: s.playing && !pending ? interval() : 0,
    jump,
    // The omniscient teletype prints the war, not every staff's signals traffic.
    log: (msg.recent ? [...msg.recent, ...msg.view.events] : msg.view.events).filter(
      (e) => msg.view.faction !== SPECTATOR || forTheRecord(e),
    ),
    truth: msg.truth,
  });
  replayStore.setState({ tick: msg.view.tick, playing });
  if (pending) {
    const p = pending;
    pending = null;
    request(p.tick, p.jump);
  } else if (playing) {
    const wait = Math.max(0, interval() - (performance.now() - requestedAt));
    timer = setTimeout(() => request(replayStore.getState().tick + 1, false), wait);
  }
}

function onMessage(msg: ReplayReply): void {
  switch (msg.t) {
    case 'progress':
      replayStore.setState({ progress: msg.endTick ? msg.tick / msg.endTick : 1 });
      return;
    case 'ready':
      replayStore.setState({
        status: 'ready',
        progress: 1,
        timeline: msg.timeline,
        verified: msg.verified,
        playing: true,
      });
      request(0, true);
      return;
    case 'frame':
      onFrame(msg);
      return;
    case 'error':
      inFlight = null;
      replayStore.setState({ status: 'error', error: msg.message, playing: false });
      return;
  }
}

/**
 * The theatre of `log`: the live game's map when it is the same generated war, else rebuilt from
 * the config (a real-world theatre is decoded, a generated one regenerated from its seed).
 */
function mapFor(log: ReplayLog, live: GameMap | null): GameMap {
  const c = log.config;
  if (live && c.theatre === undefined && live.seed === c.seed && live.factionCount === c.factions.length)
    return live;
  return mapForConfig(c);
}

// ---- public actions -----------------------------------------------------------------------------

/** The host sent the finished war's log (router). */
export function receiveReplayLog(log: ReplayLog): void {
  replayStore.setState({ log });
}

/** The live game as the player left it: parked while the replay is open, else the store. */
export function liveGame(): GameStoreState {
  return parked ?? gameStore.getState();
}

/** Screen a film from the archive (an already validated log). */
export function openArchiveReplay(log: ReplayLog): void {
  if (replayStore.getState().active) return;
  liveLog = replayStore.getState().log;
  replayStore.setState({ log });
  openReplay('archive');
}

/** Open "The Fog Lifts" for the stored log. */
export function openReplay(source: 'live' | 'archive' = 'live'): void {
  const s = replayStore.getState();
  if (!s.log || s.active) return;
  const live = gameStore.getState();
  parked = live;
  map = mapFor(s.log, live.map);
  worker = spawnWorker();
  worker.onmessage = (e: MessageEvent<ReplayReply>) => onMessage(e.data);
  worker.onerror = (e: ErrorEvent) => {
    e.preventDefault();
    replayStore.setState({
      status: 'error',
      error: e.message || 'The replay engine crashed.',
      playing: false,
    });
  };
  replayStore.setState({ ...initial, active: true, status: 'loading', source });
  // No view until the first frame: the renderer opens on the whole theatre with its pre-war
  // borders while the film is threaded, instead of on our army's final positions.
  gameStore.setState({ view: null, prevView: null, selection: [], inspectedContact: null, tool: 'select' });
  // If the live game is torn down underneath us (a multiplayer room expiring), redraw the frame.
  unwatch = gameStore.subscribe((st) => {
    if (!st.view && replayStore.getState().status === 'ready' && !inFlight)
      request(replayStore.getState().tick, true);
  });
  post({ t: 'init', log: s.log });
}

/** Close the replay and put the live game (after-action report) back as it was. */
export function closeReplay(): void {
  const { active, source } = replayStore.getState();
  if (!active) return;
  cancelTimer();
  unwatch?.();
  unwatch = null;
  worker?.terminate();
  worker = null;
  inFlight = null;
  pending = null;
  map = null;
  if (parked) gameStore.setState(parked, true);
  parked = null;
  replayStore.setState({ ...initial, ...(source === 'archive' ? { log: liveLog } : {}) });
  liveLog = null;
}

export function setPlaying(playing: boolean): void {
  const s = replayStore.getState();
  if (s.status !== 'ready' || !s.timeline) return;
  // Play at the end starts over.
  const restart = playing && s.tick >= s.timeline.endTick;
  replayStore.setState({ playing });
  if (!playing) {
    cancelTimer();
    // Settle the frame on screen (no interpolation towards a next tick that will not come).
    gameStore.setState({ tickMs: 0, paused: true });
  } else request(restart ? 0 : s.tick + 1, restart);
}

export function setSpeed(speed: ReplaySpeed): void {
  replayStore.setState({ speed });
}

export function seekTo(tick: Tick): void {
  cancelTimer();
  request(tick, true);
}

export function stepBy(hours: number): void {
  seekTo(replayStore.getState().tick + hours);
}

export function setPerspective(perspective: FactionId): void {
  if (replayStore.getState().perspective === perspective) return;
  replayStore.setState({ perspective });
  gameStore.setState({ selection: [], inspectedContact: null });
  seekTo(replayStore.getState().tick);
}

export function setTruth(truth: boolean): void {
  replayStore.setState({ truth });
  seekTo(replayStore.getState().tick);
}
